import { StyleSheet } from 'react-native-unistyles';
import { useQuery } from 'urql';
import { graphql } from '@/gql/graphql';
import useHaptics from '@/hooks/useHaptics';
import useUserId from '@/hooks/useUserId';
import { OrganizationDetailToolbarFragment } from '@/layouts/OrganizationDetail/OrganizationDetailToolbarFragment';
import { TeamDetailToolbarFragment } from '@/layouts/TeamDetail/TeamDetailToolbarFragment';
import useRootStore, {
  closeDrawer,
  gotoOrganizationDetail,
  gotoTeamDetail,
  navigateFromDrawer,
} from '@/stores/useRootStore';
import Box from '../../UI/Box';
import Card from '../../UI/Card';
import List from '../../Unistyles/List';
import { OrganizationNavigationFragment } from '../ContextNavigationFragments';
import DrawerEntityListOrganizationHeader from './DrawerEntityListOrganizationHeader';
import DrawerEntityListOrganizationItem from './DrawerEntityListOrganizationItem';
import DrawerEntityListTeamHeader from './DrawerEntityListTeamHeader';
import DrawerEntityListTeamItem from './DrawerEntityListTeamItem';

const GetDrawerEntityListOrganizations = graphql(`
  query GetDrawerEntityListOrganizations($personId: String!) {
    event(
      where: {
        event_type: { _eq: organization }
        _or: [
          { roles_by_participant: { _has_key: $personId } }
          {
            path_descendants: {
              event: { roles_by_participant: { _has_key: $personId } }
            }
          }
        ]
      }
      order_by: { title: asc }
    ) {
      id
      title
      ...OrganizationNavigation
    }
  }
`, [OrganizationNavigationFragment]);

const GetDrawerEntityListTeams = graphql(`
  query GetDrawerEntityListTeams($personId: String!) {
    team(
      where: { team_people: { person_id: { _eq: $personId } } }
      order_by: { title: asc }
    ) {
      id
      title
      ...TeamDetailToolbar
      root_event {
        ...OrganizationDetailToolbar
      }
      home_primary_color
      avatar_id
    }
  }
`, [TeamDetailToolbarFragment, OrganizationDetailToolbarFragment]);

export default function DrawerEntityList() {
  const personId = useUserId();

  const onPressOrganization = useHaptics((organizationId: string) => {
    if (useRootStore.getState().activeTab === 'organization') {
      closeDrawer();
      gotoOrganizationDetail(organizationId);
      return;
    }

    navigateFromDrawer({
      pathname: '/organization',
      params: { organizationId },
    });
  });

  const onPressTeam = useHaptics((teamId: string) => {
    if (useRootStore.getState().activeTab === 'team') {
      closeDrawer();
      gotoTeamDetail(teamId);
      return;
    }

    navigateFromDrawer({
      pathname: '/team',
      params: { teamId },
    });
  });

  const [{ data: organizationsData, fetching: organizationsFetching }] =
    useQuery({
      query: GetDrawerEntityListOrganizations,
      variables: { personId },
    });

  const [{ data: teamsData, fetching: teamsFetching }] = useQuery({
    query: GetDrawerEntityListTeams,
    variables: { personId },
  });

  const fetching = organizationsFetching || teamsFetching;
  const organizations = organizationsData?.event || [];
  const teams = teamsData?.team || [];

  const items = [
    { id: 'organizations', kind: 'organizations' as const, organizations },
    { id: 'teams', kind: 'teams' as const, teams },
  ];

  return (
    <Box flex testID="DrawerEntitiesFade">
      <List
        fetching={fetching}
        keyboardScroll={false}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={style.contentContainer}
        data={items}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => {
          if (item.kind === 'organizations') {
            return (
              <Card my="sm" mx="lg" background="surface">
                <DrawerEntityListOrganizationHeader />
                {item.organizations.length > 0 ? (
                  <>
                    <Box style={style.sectionDivider} />
                    <Box style={style.sectionItems}>
                      {item.organizations.map((organization) => (
                        <DrawerEntityListOrganizationItem
                          key={organization.id}
                          id={organization.id}
                          title={organization.title ?? ''}
                          onPress={() => onPressOrganization(organization.id)}
                        />
                      ))}
                    </Box>
                  </>
                ) : null}
              </Card>
            );
          }

          return (
            <Card my="sm" mx="lg" background="surface">
              <DrawerEntityListTeamHeader />
              {item.teams.length > 0 ? (
                <>
                  <Box style={style.sectionDivider} />
                  <Box style={style.sectionItems}>
                    {item.teams.map((team) => (
                      <DrawerEntityListTeamItem
                        key={team.id}
                        id={team.id}
                        title={team.title ?? ''}
                        onPress={() => onPressTeam(team.id)}
                        homePrimaryColor={team.home_primary_color}
                        avatarId={team.avatar_id}
                      />
                    ))}
                  </Box>
                </>
              ) : null}
            </Card>
          );
        }}
      />
    </Box>
  );
}

const style = StyleSheet.create((theme) => ({
  contentContainer: {
    paddingVertical: theme.space('sm'),
  },
  sectionDivider: {
    backgroundColor: theme.colors.borderMuted,
    height: theme.size(0.5),
    marginHorizontal: theme.space('md'),
  },
  sectionItems: {
    gap: theme.space('sm'),
    paddingHorizontal: theme.space('md'),
    paddingTop: theme.space('sm'),
    paddingBottom: theme.space('md'),
  },
}));
